// @flow
import * as React from 'react';
import { TextInput as GrommetInput, Stack } from 'grommet';
import * as Yup from 'yup';
import { get } from 'lodash';
import {
    injectIntl, defineMessages,
} from 'react-intl';
import { triggerCustomEvent, triggerNativeEvent } from 'speed-js-react-web/src/utils/events';
import FormStylesContext from 'speed-js-react-web/src/context/FormStylesContext';
import FieldWrapper from './FieldWrapper';
import StyledCloseButton from './StyledCloseButton';

type Props = {
    field: {
        onChange: (e: SyntheticInputEvent<number>) => void,
        onFocus: (e: SyntheticFocusEvent<HTMLInputElement>) => void,
        onBlur: (e: SyntheticFocusEvent<any>) => void,
        value: any;
        name: string;
    },
    form: {
        errors: Object,
        touched: Object,
        isSubmitting: boolean,
        setFieldValue: (string, any) => void,
        setFieldTouched: (string, boolean, boolean) => void,
    },
    required: boolean,
    label: string,
    help_text: string,
    disabled?: boolean,
    regex_validation?: string,
    placeholder?: string,
}

const TextInput = (props: Props): React.Node => {
    const {
        disabled,
        field: {
            name, onBlur, value,
        },
        form: {
            errors, touched, isSubmitting, setFieldValue, setFieldTouched,
        },
        label,
        help_text: help,
        required,
        regex_validation: regexValidation,
        placeholder,
    } = props;

    const inputRef = React.useRef(null);
    const clearedRef = React.useRef(false);

    const handleChange = React.useCallback((e: SyntheticInputEvent<HTMLInputElement>) => {
        setFieldValue(name, e.target.value);
        if (regexValidation) {
            // Validate field input
            setFieldTouched(name, true, true);
        }
    }, [setFieldValue, setFieldTouched, name, regexValidation]);

    const handleBlur = React.useCallback((e: SyntheticFocusEvent<HTMLInputElement>) => {
        if (onBlur) {
            onBlur(e);
        }
        setFieldValue(name, e.target.value.trim());
    }, [onBlur, setFieldValue, name]);

    const onClear = React.useCallback(() => {
        setFieldValue(name, null);
        clearedRef.current = true;
    }, [name, setFieldValue]);

    React.useEffect(() => {
        if (clearedRef.current && inputRef.current) {
            triggerNativeEvent(inputRef.current, 'change', {}, { bubbles: true });
            clearedRef.current = false;
        }
    }, [value]);

    return (
        <FormStylesContext.Consumer>
            {(formContextObject) => (
                <FieldWrapper
                    name={name}
                    required={required}
                    helpText={help}
                    label={label}
                >
                    <Stack anchor="right">
                        <GrommetInput
                            name={name}
                            ref={inputRef}
                            onChange={handleChange}
                            placeholder={placeholder}
                            onBlur={handleBlur}
                            value={value || ''}
                            id={`id-${name}`}
                            errors={get(errors, name)}
                            touched={get(touched, name)}
                            disabled={disabled || isSubmitting}
                            style={formContextObject[name]}
                        />
                        {value && <StyledCloseButton onClick={onClear} plain />}
                    </Stack>
                </FieldWrapper>
            )}
        </FormStylesContext.Consumer>
    );
};

const messages = defineMessages({
    min: {
        id: 'textinput-min',
        defaultMessage: 'The length of this field must be more than {minLimit} characters!',
    },
    max: {
        id: 'textinput-max',
        defaultMessage: 'The length of this field must be less than {maxLimit} characters!',
    },
    required: {
        id: 'textinput-required',
        defaultMessage: 'This field is required.',
    },
    inputType: {
        id: 'textinput-wrong-type',
        defaultMessage: 'Wrong input type.',
    },
});

TextInput.getValidationSchema = (field: { validation: Object }, ui) => {
    let errMsg = Yup.string().nullable();
    if (field.validation.required) {
        errMsg = Yup.string().required(messages.required).nullable();
    }
    if (field.validation.min_length) {
        errMsg = errMsg.min(field.validation.min_length.toString(),
            { ...messages.min, values: { minLimit: field.validation.min_length.toString() } });
    }
    if (field.validation.max_length) {
        errMsg = errMsg.max(field.validation.max_length.toString(),
            { ...messages.max, values: { maxLimit: field.validation.max_length.toString() } });
    }
    if (ui?.regex_validation) {
        errMsg = Yup.string().matches(new RegExp(ui.regex_validation), messages.inputType).nullable();
    }

    return errMsg;
};

TextInput.defaultProps = {
    disabled: false,
    regex_validation: undefined,
    placeholder: '',
};

export default injectIntl(TextInput);
